import "../styles/DataGrid.css";

import { Dispatch, Key, ReactNode, SetStateAction } from "react";
import ReactDataGrid, { Column as RdgColumn, SortColumn } from "react-data-grid";

import { ResultData } from "../../../../service/Shared";
import { Button, Toggle } from "../../../forms";
import { Column, Row } from "../../../layout";
import { CursorPagination } from "../../../wayfinding";
import { CustomSelectColumn } from "../components/CustomSelectColumn/CustomSelectColumn";
import { EditColumnsPanel } from "../components/EditColumnsPanel";
import { DataGridConstants } from "../constants";
import { FilterContext } from "../context";
import { ColumnDefinition, EditColumns, OnCursorChangeEventHandler } from "../models";
import { useComputeDataGridHeight } from "../utils";
import { useCursorDataGrid } from "./useCursorDataGrid";

type FilterPosition = "left" | "right" | "inventory";

export interface CursorDataGridProps {
  data: ResultData[];
  columns: ColumnDefinition[];
  // client-side filtering & sorting
  clientSide: boolean;
  // grid-level sorting enabled?
  sortable?: boolean;
  // grid-level filtering enabled?
  filterable?: boolean;
  // paging enabled?
  pageable?: boolean;
  // show total count enabled?
  showTotalCount?: boolean;
  /*
   Used for server-side sorting/filtering/paging.
   Only raised if `clientSide = false`.
   */
  onChange?: OnCursorChangeEventHandler;
  /*
    Configuration for the pagination
   */
  pagination?: {
    // mandatory for server-side
    totalCount?: number;
    pageSize?: number;
    startCursor?: string;
    endCursor?: string;
  };
  noDataMessage?: string;
  hasNoPadding?: boolean;
  hasBottomMargin?: boolean;
  dataIsLoading?: boolean;
  loadingMessage?: string;
  defaultSortingCriteria?: SortColumn[];
  refresh?: boolean;
  triggerPagePrevious?: boolean;
  onRowClick?: (e: ResultData) => void;
  showFilterToggle?: boolean;
  filterPosition?: FilterPosition;
  // export table button
  exportable?: boolean;
  exportButtonText?: string;
  exportButtonIcon?: ReactNode;
  exportFileName?: string;
  onExternalExport?: boolean;
  filterExportRows?: (columns: ResultData[]) => ResultData[];
  filterExportColumns?: (columns: RdgColumn<ResultData, unknown>[]) => RdgColumn<ResultData, unknown>[];
  // pagination on vertical scroll
  infiniteScroll?: boolean;
  // change the order, enable & disable data grid columns, store in local storage
  showEditColumnsPanel?: boolean;
  closeEditColumnsPanel?: () => void;
  editColumnsLocalStorageItemName?: string;
  editColumnsText?: string;
  defaultEditColumns?: EditColumns;
  hasScrollButton?: boolean;
  hasTotalRow?: boolean;
  totalColumnNames?: string[]; // the names of the columns to calculate totals for
  // For row selection
  rowKeyGetter?: (row: ResultData) => Key;
  selectedRows?: ReadonlySet<Key>;
  setSelectedRows?: Dispatch<SetStateAction<ReadonlySet<Key>>>;
  variant?: "default" | "seed";
}

export const CursorDataGrid = ({
  data,
  columns,
  filterable = false,
  sortable = true,
  clientSide = false,
  pageable = true,
  showTotalCount = true,
  defaultSortingCriteria,
  onChange,
  pagination = { pageSize: 10 },
  noDataMessage,
  loadingMessage = "Loading...",
  hasNoPadding = false,
  hasBottomMargin = true,
  dataIsLoading,
  refresh,
  onRowClick,
  showFilterToggle,
  filterPosition = "left",
  triggerPagePrevious,
  onExternalExport,
  exportable = false,
  exportButtonText = "Export audit",
  exportButtonIcon,
  exportFileName,
  filterExportRows,
  filterExportColumns,
  infiniteScroll = false,
  showEditColumnsPanel = false,
  closeEditColumnsPanel,
  editColumnsLocalStorageItemName,
  editColumnsText = "Select the columns to display in the table. Drag headings to rearrange their left-to-right order.",
  defaultEditColumns,
  hasTotalRow = false,
  totalColumnNames,
  hasScrollButton = true,
  selectedRows,
  setSelectedRows,
  rowKeyGetter = (row: ResultData): string => {
    return row.uuid as string;
  },
  variant = "default",
}: CursorDataGridProps): JSX.Element => {
  const {
    resultData,
    filterCriteria,
    setFilterCriteria,
    filteredRdgColumns,
    sortColumns,
    onSort,
    onPrevious,
    onNext,
    hasNext,
    hasPrevious,
    totalCount,
    totalRow,
    filtersEnabled,
    setFiltersEnabled,
    showFilterToggleSwitch,
    onExport,
    onVerticalScroll,
    onRefreshColumns,
    dataGridRef,
    iconArrow,
    iconPos,
    handleScrollClick,
    onHorizontalScroll,
    showScrollButton,
    selectableUuids,
  } = useCursorDataGrid({
    data,
    columns,
    filterable,
    sortable,
    clientSide,
    pageable,
    defaultSortingCriteria,
    onChange,
    pagination,
    refresh,
    showFilterToggle,
    triggerPagePrevious,
    onExternalExport,
    exportFileName,
    filterExportRows,
    filterExportColumns,
    infiniteScroll,
    editColumnsLocalStorageItemName,
    totalColumnNames,
  });

  // let gridLengthBuffer = 0;
  // if (variant === "seed") gridLengthBuffer = filtersEnabled ?
  const { computedHeight } = useComputeDataGridHeight(
    resultData.length,
    DataGridConstants.rowHeight,
    hasTotalRow,
    filtersEnabled,
    variant,
    showScrollButton
  );

  // Helper for header state
  const allVisibleAllowedSelected =
    selectableUuids.size > 0 && Array.from(selectableUuids).every((id) => selectedRows?.has(id));

  // Optionally prepend the SelectColumn (keeps edit-columns feature intact)
  const rdgColumnsWithSelect: RdgColumn<ResultData>[] =
    selectedRows && setSelectedRows
      ? [
          CustomSelectColumn({
            selectableUuids,
            allVisibleAllowedSelected,
            selectedRows,
            setSelectedRows,
            rowKeyGetter,
          }),
          ...filteredRdgColumns,
        ]
      : filteredRdgColumns;

  const exportButtons = (): JSX.Element => {
    return (
      <div className="ExportButtonsContainer">
        <Button text={exportButtonText} onClick={onExport} icon={exportButtonIcon} iconPosition="left" size="small" />
      </div>
    );
  };

  const filterButtons = (): JSX.Element => {
    // eslint-disable-next-line no-nested-ternary
    const colJustify = filterPosition === "right" ? "end" : undefined;
    const rowSpacing = filterPosition === "right" ? "xl" : "s";
    const labelAlignment = filterPosition === "right" ? "left" : "right";
    return (
      <Row spacingV={rowSpacing}>
        <Column
          span={12}
          className={`FilterButtonsContainer${exportable ? " HasExportButton" : ""}${filterPosition === "inventory" ? " InventoryFilter" : ""}`}
          justify={colJustify}
        >
          {filterable && showFilterToggleSwitch && (
            <Toggle
              label={`${filtersEnabled ? "Hide filters" : "Show filters"}`}
              labelAlignment={labelAlignment}
              value={filtersEnabled}
              onChange={() => {
                if (filtersEnabled) {
                  setFilterCriteria([]);
                }
                setFiltersEnabled(!filtersEnabled);
              }}
            />
          )}
          {filterable && filterCriteria.length > 0 && (
            <button
              className="BtnLink"
              onClick={() => {
                setFilterCriteria([]);
              }}
            >
              Clear all filters
            </button>
          )}
          {showScrollButton && hasScrollButton && (
            <Button
              variant="circular"
              text="scroll"
              hasNoPadding
              className={`scrollButton ${filterPosition === "left" ? "paddingLeft" : ""}`}
              corners="rounded"
              icon={iconArrow}
              size="custom"
              onClick={handleScrollClick}
              iconPosition={iconPos}
            />
          )}
        </Column>
      </Row>
    );
  };
  return (
    <div
      className={`DataGridContainer${onRowClick ? " ClickableRows" : ""}${infiniteScroll ? " InfiniteScroll" : ""} ${variant}`}
    >
      {resultData && (!!resultData.length || (!resultData.length && !!filterCriteria.length)) ? (
        <>
          {exportable && exportButtons()}
          {(filterable || (showScrollButton && hasScrollButton)) && filterButtons()}
          <div
            onScroll={onVerticalScroll}
            className={`DataGrid ${filtersEnabled ? "FiltersApplied" : ""} ${hasTotalRow ? "HasTotalRow" : ""} ${variant} ${showScrollButton ? "ShowScrollButton" : ""}`}
          >
            <Row spacingV={pageable && hasBottomMargin ? "ml" : undefined}>
              <Column span={12} hasNoPadding={hasNoPadding}>
                <FilterContext.Provider value={filterCriteria}>
                  <ReactDataGrid
                    style={{ height: computedHeight }}
                    rows={hasTotalRow ? ([totalRow, ...resultData] as ResultData[]) : resultData}
                    columns={rdgColumnsWithSelect}
                    defaultColumnOptions={{ sortable }}
                    className="rdg-light"
                    rowHeight={DataGridConstants.rowHeight}
                    headerRowHeight={filterable && filtersEnabled ? 2 * DataGridConstants.rowHeight : undefined}
                    sortColumns={sortColumns}
                    onSortColumnsChange={onSort}
                    onRowClick={onRowClick}
                    ref={dataGridRef}
                    onScroll={onHorizontalScroll}
                    rowKeyGetter={selectedRows && setSelectedRows && rowKeyGetter}
                    selectedRows={selectedRows}
                    onSelectedRowsChange={setSelectedRows}
                  />
                </FilterContext.Provider>
              </Column>
            </Row>
            {showTotalCount && (
              <Row spacingV={pageable ? "ml" : undefined}>
                <div className="DataGridResultCount">
                  <span className="body2">{totalCount} results</span>
                </div>
              </Row>
            )}
            {!infiniteScroll && (
              <Row>
                <Column span={12}>
                  {pageable && (
                    <CursorPagination
                      onPrevious={onPrevious}
                      onNext={onNext}
                      hasNext={hasNext}
                      hasPrevious={hasPrevious}
                    />
                  )}
                </Column>
              </Row>
            )}
          </div>
        </>
      ) : (
        <p className="body2 NoData">{dataIsLoading ? loadingMessage : noDataMessage}</p>
      )}
      {closeEditColumnsPanel !== undefined && editColumnsLocalStorageItemName && (
        <EditColumnsPanel
          masterColumns={columns}
          editColumnsLocalStorageItemName={editColumnsLocalStorageItemName}
          editColumnsText={editColumnsText}
          defaultEditColumns={defaultEditColumns}
          show={showEditColumnsPanel}
          onClose={closeEditColumnsPanel}
          onRefreshColumns={onRefreshColumns}
        />
      )}
    </div>
  );
};
